import React from 'react';
import {Spinner} from 'react-bootstrap';
import {useNavigate} from 'shared/reload/helper/useNavigate';
import {useConfirmationDialog} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {genericMessageHandler, jobId} from 'shared/helpers';
import {copyJob, unSubmitJob, deleteJob} from 'service/JobService';
import {AxiosResponseOrError} from 'service/Axios';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {jobSearch} from 'hooks';
import {copyJobMessage} from 'components/customer/Job/helpers/getCopyJobMessage';
import {Job} from 'shared/types/PartialJob';
import {JOB_STATUS} from 'components/customer/Dashboard/LastJobButton';
import {useAppDispatch} from 'store/customer';
import {invalidateJobs} from 'components/customer/Job/store/jobApi';

const STALE_STATUS_HTTP_CODE = 409;
const STALE_STATUS_TOAST_DURATION = 3000;

const dialogHandler = (
    message = '',
    loader = false,
    optMessages: string[] = []
) => {
    return (
        <>
            {loader ? (
                <Spinner
                    animation="border"
                    style={{
                        width: '15px',
                        height: '15px',
                        color: '#AAA',
                    }}
                />
            ) : null}

            <span className="searchText">{message}</span>

            {optMessages?.map((optMsg) => {
                return (
                    <div
                        key={optMsg}
                        className={`searchText ${loader && 'opt_message'}`}>
                        {optMsg}
                    </div>
                );
            })}
        </>
    );
};

const jobActionHandlers = () => {
    const {
        refreshDashboard,
        setRefreshDashboard,
        setSuccessDialog,
        userProfile,
    } = useAppContext();
    const {dialog, showDialog, hideDialog} = useConfirmationDialog();
    const {notify} = useNotificationContext();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {resetResults} = jobSearch();

    const deleteJobHandler = (job: Job) => {
        const formattedJobId = jobId(job.id);

        showDialog({
            title: `Delete Job ID #${formattedJobId}?`,
            message: `Are you sure you want to delete this Job?`,
            keyboard: false,
            yes: async () => {
                try {
                    await deleteJob(job.id);
                    resetResults(true);

                    genericMessageHandler(
                        notify,
                        {message: `Job ID #${formattedJobId} was deleted!`},
                        'success'
                    );

                    if (setRefreshDashboard && refreshDashboard) {
                        setRefreshDashboard({
                            status: !refreshDashboard['status'],
                        });
                    }
                    navigate(`/v2/`);
                } catch (error) {
                    genericMessageHandler(notify, error);
                }
            },
        });
    };

    const showJobStatus = ({
        id,
        webhookResponse,
    }: {
        id: number;
        webhookResponse: string[];
    }) => {
        const formattedJobId = jobId(id);

        const message =
            webhookResponse.length > 1 ? (
                <ul>
                    {webhookResponse.map((r) => (
                        <li key={r}>{r}</li>
                    ))}
                </ul>
            ) : (
                webhookResponse[0]
            );

        showDialog({
            title: `Status of #${formattedJobId}?`,
            message: message,
            keyboard: false,
            hideYesButton: true,
            hideNoButton: true,
            buttons: [
                {
                    name: 'Ok',
                    show: true,
                    variant: 'primary',
                    action: () => {
                        hideDialog();
                    },
                },
            ],
        });
    };

    const unSubmitJobHandler = (jobs: {id: number}) => {
        const formattedJobId = jobId(jobs.id);

        showDialog({
            title: `Unsubmit Job ID #${formattedJobId}?`,
            message: `Are you sure you want to unsubmit this job for further edits?`,
            keyboard: false,
            yes: async () => {
                try {
                    await unSubmitJob(jobs.id);
                    resetResults(true);
                    genericMessageHandler(
                        notify,
                        {
                            message: `Unsubmitted Job ID #${formattedJobId} Successfully!`,
                        },
                        'success'
                    );

                    if (setRefreshDashboard && refreshDashboard) {
                        setRefreshDashboard({
                            status: !refreshDashboard['status'],
                        });
                    }

                    if (setSuccessDialog) {
                        setSuccessDialog([
                            {
                                status: true,
                                message: `Successfully unsubmitted <b>Job ID #${formattedJobId}</b><br />Do you want to continue to edit this job?`,
                                link: `/v2/job/${formattedJobId}/dashboard`,
                            },
                        ]);
                    }

                    navigate(`/v2/`);
                } catch (error) {
                    genericMessageHandler(
                        notify,
                        error,
                        'danger',
                        true,
                        STALE_STATUS_TOAST_DURATION
                    );

                    if (
                        (error as AxiosResponseOrError).status ===
                        STALE_STATUS_HTTP_CODE
                    ) {
                        dispatch(invalidateJobs());
                    }
                }
            },
        });
    };

    const editJobHandler = (job: Job) => {
        const formattedJobId = jobId(job.id);
        resetResults(true);

        if (
            userProfile?.jobConfirmationEnabled &&
            job.accepted &&
            job.status == JOB_STATUS.JOB_PENDING_CONFIRMATION
        ) {
            navigate(`/v2/job/${formattedJobId}/confirmation`);
        } else {
            navigate(`/v2/job/${formattedJobId}/dashboard`);
        }
    };

    const copyJobHandler = (job: Job) => {
        const formattedJobId = jobId(job.id);

        showDialog({
            title: `Copy Job ID #${formattedJobId}?`,
            message: copyJobMessage(job, userProfile),
            hideYesButton: true,
            hideNoButton: true,
            keyboard: false,
            buttons: [
                {
                    name: `Copy Job #${formattedJobId}`,
                    show: true,
                    action: async () => {
                        showDialog({
                            title: `One moment please...`,
                            message: dialogHandler(
                                `Copying JobID# ${formattedJobId}`,
                                true,
                                [``, `Page will refresh upon completion`]
                            ),
                            hideYesButton: true,
                            hideNoButton: true,
                            keyboard: false,
                        });

                        const copiedJob = (await copyJob(job.id)) as {
                            jobId: number;
                        };

                        resetResults(true);

                        if (setRefreshDashboard && refreshDashboard) {
                            setRefreshDashboard({
                                status: !refreshDashboard['status'],
                            });
                        }

                        if (setSuccessDialog) {
                            setSuccessDialog([
                                {
                                    type: 'copy',
                                    status: true,
                                    message: `Successfully copied <b>Job ID #${formattedJobId}</b> to <b>NEW Job ID #${copiedJob['jobId']}</b><br />The price is currently being finalised to ensure accuracy. Please check back shortly.`,
                                    link: `/v2/job/${copiedJob['jobId']}/dashboard`,
                                },
                            ]);
                        }

                        hideDialog();
                        navigate(`/v2/`);
                    },
                },
                {
                    name: 'Close',
                    show: true,
                    variant: 'danger',
                    action: () => {
                        hideDialog();
                    },
                },
            ],
        });
    };

    return {
        navigate,
        showDialog,
        dialog,
        copyJobHandler,
        unSubmitJobHandler,
        deleteJobHandler,
        editJobHandler,
        showJobStatus,
    };
};

export {jobActionHandlers, dialogHandler};
